/* eslint-disable react/prop-types */
import { useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import "../../../i18n";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import { buildChoiceFormLivePreview } from "../Cms/MenuTree/menuTreeUtils";
import "./AddCategory.css";

const EMPTY_LIVE_DRAFT = { visualSettingsMode: "custom" };

const AddChoice = ({ onSuccess, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);
  const embedded = Boolean(onSuccess || onClose);

  const [minSelected, setMinSelected] = useState(0);
  const [maxSelected, setMaxSelected] = useState(1);
  const [obligatory, setObligatory] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openLangs, setOpenLangs] = useState({ en: true });
  const [choiceDetails, setChoiceDetails] = useState(
    languages.reduce((acc, lang) => {
      acc[lang.code] = { choiceTitle: "" };
      return acc;
    }, {})
  );

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) =>
    setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const openLanguages = languages.filter((lang) => isLangOpen(lang.code));

  const livePreview = useMemo(
    () =>
      buildChoiceFormLivePreview({
        titles: Object.fromEntries(
          Object.entries(choiceDetails).map(([code, details]) => [
            code,
            details?.choiceTitle || "",
          ])
        ),
        minSelected,
        maxSelected,
        obligatory,
      }),
    [choiceDetails, minSelected, maxSelected, obligatory]
  );

  const handleLangInputChange = (lang, field, value) => {
    setChoiceDetails((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (maxSelected === 0) {
      swal(t("warningTitle"), t("warningMaxItemsToSelect"), t("warning"));
      return;
    }
    if (minSelected > maxSelected) {
      swal(t("warningTitle"), t("warningMinItemsToSelect"), t("warning"));
      return;
    }
    if (!choiceDetails || Object.keys(choiceDetails).length === 0) {
      swal(t("warningTitle"), t("requiredData"), t("warning"));
      return;
    }
    if (obligatory && minSelected === 0) {
      swal(t("warningTitle"), t("warningMin"), t("warning"));
      return;
    }

    const choiceDetailsToSend = Object.entries(choiceDetails)
      .map(([lang, details]) => ({
        language: lang,
        choiceTitle: details.choiceTitle?.trim(),
      }))
      .filter((detail) => detail.choiceTitle);

    const englishEntry = choiceDetailsToSend.find((detail) => detail.language === "en");
    if (!englishEntry) {
      swal(t("warningTitle"), t("englishDataRequired"), t("warning"));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${config.dev}/v2/choices/create`,
        {
          data: JSON.stringify({
            choiceDetails: choiceDetailsToSend,
            minSelected,
            maxSelected,
            obligatory,
            isFree,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
          },
        }
      );

      swal({
        title: t("successTitle"),
        text: t("choiceCreated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      }).then(() => {
        if (onSuccess) onSuccess();
        else navigate("/choices");
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout du choix :", error.response?.data || error.message);
      swal(t("warningTitle"), t("failedAddProduct") || "Failed to add choice.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const form = (
    <form
      className={`catform${embedded ? " catform--embed" : ""}`}
      onSubmit={handleSubmit}
      noValidate
    >
      {!embedded && (
        <div className="catform__top">
          <h1 className="catform__title">{t("choiceFormTitle")}</h1>
          <button type="button" className="btn catform__link" onClick={() => navigate("/choices")}>
            <i className="fas fa-arrow-left" aria-hidden /> {t("choicesList")}
          </button>
        </div>
      )}

      <section className="catform__section">
        <h2 className="catform__heading">{t("catFormStepName")}</h2>
        <p className="catform__pills-label">{t("choiceFormChooseLanguage")}</p>
        <div className="catform__pills">
          {languages.map((lang) => {
            const filled = Boolean(choiceDetails[lang.code]?.choiceTitle?.trim());
            return (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill ${isLangOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                {filled && <i className="fas fa-check" aria-hidden />}
              </button>
            );
          })}
        </div>
        <div className="catform__lang-fields">
          {openLanguages.map((lang) => (
            <div className="catform__lang-panel" key={lang.code}>
              <div className="catform__field">
                <label className="catform__label" htmlFor={`choice-title-${lang.code}`}>
                  <img src={lang.flag} alt="" />
                  {lang.label} — {t("title")}
                </label>
                <input
                  id={`choice-title-${lang.code}`}
                  type="text"
                  className="catform__input"
                  value={choiceDetails[lang.code]?.choiceTitle || ""}
                  autoFocus={lang.code === "en"}
                  dir={lang.code === "ar" ? "rtl" : "ltr"}
                  onChange={(e) => handleLangInputChange(lang.code, "choiceTitle", e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="catform__section">
        <h2 className="catform__heading">{t("choiceFormRules")}</h2>
        <p className="catform__help">{t("choiceFormRulesHelp")}</p>
        <div className="catform__pair">
          <div className="catform__field">
            <label className="catform__label" htmlFor="choice-min">
              {t("minSelection")}
            </label>
            <input
              id="choice-min"
              type="number"
              min={0}
              className="catform__input"
              value={minSelected}
              onChange={(e) => setMinSelected(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="catform__field">
            <label className="catform__label" htmlFor="choice-max">
              {t("maxSelection")}
            </label>
            <input
              id="choice-max"
              type="number"
              min={1}
              className="catform__input"
              value={maxSelected}
              onChange={(e) => setMaxSelected(parseInt(e.target.value, 10) || 0)}
            />
          </div>
        </div>
        <div className="catform__radios catform__radios--options">
          <label className={`catform__radio ${obligatory ? "is-on" : ""}`}>
            <input
              type="checkbox"
              checked={obligatory}
              onChange={() => setObligatory((v) => !v)}
            />
            {t("hotRequired")}
          </label>
          <label className={`catform__radio ${isFree ? "is-on" : ""}`}>
            <input type="checkbox" checked={isFree} onChange={() => setIsFree((v) => !v)} />
            {t("freeChoice")}
          </label>
        </div>
      </section>

      <div className="catform__actions">
        {embedded ? (
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => onClose?.()}
          >
            {t("cancel")}
          </button>
        ) : (
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate("/choices")}
          >
            {t("cancel")}
          </button>
        )}
        <button type="submit" className="btn catform__btn catform__btn--success" disabled={submitting}>
          {submitting ? t("catFormCreating") : t("addChoice")}
        </button>
      </div>
    </form>
  );

  const preview = (
    <aside className="catform-live" aria-label={t("wizardPreviewLive")}>
      <WizardLivePhone
        draft={EMPTY_LIVE_DRAFT}
        compact
        showStepLabel={false}
        productDetail={livePreview}
      />
    </aside>
  );

  const layout = (
    <div className="catform-with-preview">
      {form}
      {preview}
    </div>
  );

  if (embedded) return layout;

  return (
    <>
      <PageTitle activeMenu={t("addChoice")} motherMenu={t("kitchen")} />
      {layout}
    </>
  );
};

export default AddChoice;
